<template>
  <main class="home-page bv-page">
    <div class="bv-shell">
      <section class="bv-hero home-hero">
        <div class="home-hero-copy">
          <span class="bv-eyebrow">{{ homeContent.hero.badge }}</span>
          <h1 class="bv-title">
            {{ heroTitle.before }}<span v-if="heroTitle.accent" class="accent">{{ heroTitle.accent }}</span>{{ heroTitle.after }}
          </h1>
          <p class="bv-lead">{{ homeContent.hero.copy }}</p>
          <div class="bv-actions">
            <router-link class="bv-btn primary" :to="routeTo('/ecosystem')">
              {{ homeContent.hero.primary }}
            </router-link>
            <router-link class="bv-btn secondary" :to="routeTo('/academy/papers')">
              {{ homeContent.hero.secondary }}
            </router-link>
          </div>
          <p class="home-hero-note" :aria-label="homeContent.hero.note">
            <span class="terminal-prompt" aria-hidden="true">&gt;</span>
            <span class="typing-output" aria-hidden="true">{{ typedHeroNote }}</span>
          </p>
        </div>

        <aside class="home-logo-panel" aria-label="BullVerge 3D logo">
          <div class="home-logo-frame">
            <ModelViewer />
          </div>
        </aside>
      </section>

      <section class="home-partner" data-reveal>
        <div>
          <span class="bv-kicker">{{ homeContent.partner.eyebrow }}</span>
          <h2>{{ homeContent.partner.title }}</h2>
          <p>{{ homeContent.partner.copy }}</p>
        </div>
        <a
          class="bv-btn secondary"
          href="https://aws.amazon.com/startups/showcase/startup-details/8b62552e-c0dd-4d37-ba5b-984484376ca7"
          target="_blank"
          rel="noopener"
        >
          {{ homeContent.partner.link }}
        </a>
      </section>

      <section class="bv-section bv-split">
        <div class="bv-section-head">
          <span class="bv-kicker">{{ homeContent.mission.eyebrow }}</span>
          <h2>{{ homeContent.mission.title }}</h2>
          <p>{{ homeContent.mission.copy }}</p>
        </div>
        <div class="bv-card mission-card">
          <ul class="bv-list">
            <li v-for="point in homeContent.mission.points" :key="point">{{ point }}</li>
          </ul>
          <div class="home-metric-grid">
            <div v-for="metric in ecosystemContent.ecosystem.promise" :key="metric.label" class="home-metric">
              <strong>{{ metric.label }}</strong>
              <span>{{ metric.value }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="bv-section">
        <header class="bv-section-head">
          <span class="bv-kicker">{{ homeContent.ecosystem.eyebrow }}</span>
          <h2>{{ homeContent.ecosystem.title }}</h2>
          <p>{{ homeContent.ecosystem.copy }}</p>
          <div class="bv-actions">
            <router-link class="bv-btn primary" :to="routeTo('/ecosystem/products')">
              {{ homeContent.ecosystem.productsCta }}
            </router-link>
            <router-link class="bv-btn secondary" :to="routeTo('/ecosystem/modules')">
              {{ homeContent.ecosystem.modulesCta }}
            </router-link>
          </div>
        </header>

        <div class="bv-grid four">
          <article v-for="product in featuredProducts" :key="product.id" class="bv-card product-card">
            <SignalGlyph :name="product.icon" size="sm" />
            <h3>{{ product.name }}</h3>
            <p>{{ product.short }}</p>
            <router-link class="bv-card-link" :to="routeTo(product.path)">
              {{ product.eyebrow }}
            </router-link>
          </article>
        </div>
      </section>

      <section class="bv-section home-aegis-modules">
        <article class="bv-card">
          <span class="bv-kicker">{{ homeContent.aegis.eyebrow }}</span>
          <h2>{{ homeContent.aegis.title }}</h2>
          <p>{{ homeContent.aegis.copy }}</p>
          <ul class="bv-list">
            <li v-for="point in homeContent.aegis.points" :key="point">{{ point }}</li>
          </ul>
          <router-link class="bv-btn secondary" :to="routeTo('/aegis')">
            {{ homeContent.aegis.cta }}
          </router-link>
        </article>

        <article class="bv-card modules-preview">
          <span class="bv-kicker">{{ homeContent.modulesPreview.eyebrow }}</span>
          <h2>{{ homeContent.modulesPreview.title }}</h2>
          <p>{{ homeContent.modulesPreview.copy }}</p>
          <div class="module-chip-grid">
            <span v-for="module in featuredModules" :key="module.id" class="bv-chip">
              {{ module.name }}
            </span>
          </div>
          <router-link class="bv-btn secondary" :to="routeTo('/ecosystem/modules')">
            {{ homeContent.ecosystem.modulesCta }}
          </router-link>
        </article>
      </section>

      <section class="bv-section">
        <header class="bv-section-head">
          <span class="bv-kicker">{{ homeContent.impact.eyebrow }}</span>
          <h2>{{ homeContent.impact.title }}</h2>
          <p>{{ homeContent.impact.copy }}</p>
        </header>
        <div class="bv-grid">
          <article v-for="item in impactAreas" :key="item.title" class="bv-card">
            <SignalGlyph :name="item.icon" size="sm" />
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </article>
        </div>
      </section>

      <section class="bv-section bv-split">
        <div class="bv-section-head">
          <span class="bv-kicker">{{ homeContent.strategic.eyebrow }}</span>
          <h2>{{ homeContent.strategic.title }}</h2>
          <p>{{ homeContent.strategic.copy }}</p>
        </div>
        <div class="strategic-list">
          <article v-for="item in homeContent.strategic.items" :key="item.title" class="bv-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </article>
          <p class="bv-note">{{ homeContent.strategic.note }}</p>
        </div>
      </section>

      <section class="bv-section validation-section">
        <div class="bv-section-head">
          <span class="bv-kicker">{{ homeContent.validation.eyebrow }}</span>
          <h2>{{ homeContent.validation.title }}</h2>
          <p>{{ homeContent.validation.copy }}</p>
        </div>
        <div class="bv-grid two">
          <article class="bv-card">
            <ul class="bv-list">
              <li v-for="item in homeContent.validation.items" :key="item">{{ item }}</li>
            </ul>
          </article>
          <article class="bv-card faq-card">
            <h3>{{ homeContent.faqCta.title }}</h3>
            <p>{{ homeContent.faqCta.copy }}</p>
            <router-link class="bv-btn primary" :to="routeTo('/academy/faq')">
              {{ homeContent.faqCta.cta }}
            </router-link>
          </article>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHead } from '@vueuse/head'
import ModelViewer from '@/components/ModelViewer.vue'
import SignalGlyph from '@/components/SignalGlyph.vue'
import { i18nState } from '@/i18n'
import { getHomeContent } from '@/i18n/homeContent'
import { getEcosystemContent } from '@/i18n/ecosystemContent'
import { buildPageHead } from '@/utils/pageHead'

function splitHeroTitle(title, accent) {
  if (!accent || !title.includes(accent)) {
    return { before: title, accent: '', after: '' }
  }

  const [before, after] = title.split(accent)
  return { before, accent, after }
}

function buildHomeSchema(locale, content) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'BullVerge',
    description: content.seo.description,
    publisher: {
      '@type': 'Organization',
      name: 'BullVerge'
    },
    potentialAction: {
      '@type': 'ReadAction',
      target: locale === 'en'
        ? 'https://www.bullverge.it/ecosystem?lang=en'
        : 'https://www.bullverge.it/ecosystem'
    }
  }
}

export default {
  name: 'HomePage',
  components: { ModelViewer, SignalGlyph },
  setup() {
    const homeContent = computed(() => getHomeContent(i18nState.locale))
    const ecosystemContent = computed(() => getEcosystemContent(i18nState.locale))
    const featuredProducts = computed(() => ecosystemContent.value.products)
    const featuredModules = computed(() => ecosystemContent.value.modules)
    const impactAreas = computed(() => ecosystemContent.value.impact)
    const heroTitle = computed(() => splitHeroTitle(homeContent.value.hero.title, homeContent.value.hero.accent))
    const typedHeroNote = ref('')
    let typingTimer = null
    const routeTo = (path) => {
      const route = { path }
      if (i18nState.locale === 'en') route.query = { lang: 'en' }
      return route
    }

    const clearTypingTimer = () => {
      if (!typingTimer) return
      window.clearTimeout(typingTimer)
      typingTimer = null
    }

    const runHeroTyping = () => {
      const note = homeContent.value.hero.note || ''
      clearTypingTimer()

      if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        typedHeroNote.value = note
        return
      }

      typedHeroNote.value = ''
      let index = 0
      const typeNextCharacter = () => {
        index += 1
        typedHeroNote.value = note.slice(0, index)
        if (index >= note.length) {
          typingTimer = window.setTimeout(runHeroTyping, 5000)
          return
        }
        typingTimer = window.setTimeout(typeNextCharacter, 34)
      }

      typingTimer = window.setTimeout(typeNextCharacter, 180)
    }

    onMounted(runHeroTyping)
    onBeforeUnmount(clearTypingTimer)
    watch(() => homeContent.value.hero.note, runHeroTyping)

    useHead(computed(() => {
      const locale = i18nState.locale
      const content = homeContent.value
      return buildPageHead({
        title: content.seo.title,
        description: content.seo.description,
        path: '/',
        locale,
        imageAlt: content.seo.imageAlt,
        schemaKey: 'home-schema',
        schema: buildHomeSchema(locale, content)
      })
    }))

    return {
      homeContent,
      ecosystemContent,
      featuredProducts,
      featuredModules,
      impactAreas,
      heroTitle,
      typedHeroNote,
      routeTo
    }
  }
}
</script>

<style scoped>
.home-page {
  position: relative;
  overflow: hidden;
  background: var(--bv-page-background);
}

.home-page::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    var(--bv-tactical-grid);
  background-size: 42px 42px;
  mask-image: linear-gradient(180deg, black 0%, black 76%, rgba(0, 0, 0, 0.84) 100%);
  opacity: 0.86;
}

.home-page::after {
  content: '';
  position: absolute;
  inset: auto -10% -18% -10%;
  height: 52%;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 100%, rgba(255, 197, 134, 0.08), transparent 62%),
    linear-gradient(180deg, transparent 0%, rgba(190, 196, 196, 0.08) 48%, rgba(210, 205, 196, 0.12) 100%);
  filter: blur(10px);
}

.home-hero-copy {
  min-width: 0;
}

.home-hero-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  min-height: 3.2em;
  max-width: 78ch;
  margin: 1.25rem 0 0;
  color: rgba(196, 248, 159, 0.9);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.86rem;
  line-height: 1.6;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  text-shadow: 0 0 12px rgba(196, 248, 159, 0.2);
}

.terminal-prompt {
  flex: 0 0 auto;
  color: rgba(255, 197, 134, 0.95);
}

.typing-output::after {
  content: '';
  display: inline-block;
  width: 0.62ch;
  height: 1.05em;
  margin-left: 0.22ch;
  vertical-align: -0.16em;
  background: rgba(196, 248, 159, 0.86);
  box-shadow: 0 0 12px rgba(196, 248, 159, 0.4);
  animation: terminal-cursor 0.85s steps(1) infinite;
}

.home-logo-panel {
  display: grid;
  gap: 0.85rem;
  align-content: start;
}

.home-logo-frame {
  position: relative;
  display: grid;
  place-items: center;
  min-height: clamp(330px, 46vw, 560px);
  border-radius: 0;
  border: 0;
  background: transparent;
  overflow: visible;
}

@keyframes terminal-cursor {
  0%,
  48% {
    opacity: 1;
  }

  49%,
  100% {
    opacity: 0;
  }
}

.home-partner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--bv-tactical-stroke);
  border-radius: var(--bv-card-radius);
  background: var(--bv-card-bg);
  box-shadow: var(--bv-card-shadow);
}

.home-partner h2 {
  margin: 0.55rem 0 0.2rem;
  font-size: clamp(1.25rem, 2vw, 1.65rem);
}

.home-partner p {
  margin: 0;
  color: var(--text-soft);
}

.mission-card {
  gap: 1.2rem;
}

.home-metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;
}

.home-metric {
  display: grid;
  gap: 0.25rem;
  padding: 0.8rem;
  border: 1px solid var(--bv-tactical-stroke-strong);
  border-radius: var(--bv-card-radius);
  background: rgba(196, 248, 159, 0.06);
}

.home-metric strong {
  color: var(--accent-lime);
  font-size: 0.78rem;
  text-transform: uppercase;
}

.home-metric span {
  color: var(--text-main);
  line-height: 1.35;
}

.product-card {
  min-height: 260px;
}

.home-aegis-modules {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
}

.home-aegis-modules .bv-card {
  gap: 1rem;
}

.module-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.48rem;
}

.strategic-list {
  display: grid;
  gap: 1rem;
}

.faq-card {
  justify-content: start;
}

@media (max-width: 1020px) {
  .home-aegis-modules {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .home-partner {
    display: grid;
  }

  .home-metric-grid {
    grid-template-columns: 1fr;
  }

  .home-logo-frame {
    min-height: 340px;
  }
}
</style>
